/**
 * Read Meter - Track Firestore reads for caching optimization
 * Enables measurement of before/after read counts to quantify caching wins
 */

export const readMeter = { 
  total: 0, 
  byRoute: {},
  byLabel: {},
  sessionStart: Date.now()
};

/**
 * Wrapper for Firestore reads to track usage
 * @param {Function} fn - The Firestore function to measure
 * @param {string} label - Label for the read operation
 * @param {string} route - Route path (defaults to current location)
 * @returns {Promise} Result of the Firestore operation
 */
export async function measuredGet(fn, label, route = window.location.pathname) {
  const res = await fn();
  
  try {
    // Check if read meter is enabled (default to true in development)
    const isEnabled = import.meta.env?.VITE_READ_METER_ENABLED === 'true' || 
                     (import.meta.env?.DEV && import.meta.env?.VITE_READ_METER_ENABLED !== 'false');
    
    if (isEnabled) {
      readMeter.total += 1;
      readMeter.byRoute[route] = (readMeter.byRoute[route] || 0) + 1;
      readMeter.byLabel[label] = (readMeter.byLabel[label] || 0) + 1;
      
      // Log in development
      if (import.meta.env?.DEV) {
        console.table({ 
          total: readMeter.total, 
          route, 
          routeReads: readMeter.byRoute[route], 
          label,
          labelReads: readMeter.byLabel[label],
          sessionDuration: Math.round((Date.now() - readMeter.sessionStart) / 1000) + 's'
        });
      }
    }
  } catch (error) {
    // If there's any error with environment variables, just continue without measurement
    console.warn('📊 READ METER: Error checking environment, continuing without measurement:', error);
  }
  
  return res;
}

/**
 * Get current read statistics
 * @returns {Object} Read statistics
 */
export function getReadStats() {
  return {
    total: readMeter.total,
    byRoute: { ...readMeter.byRoute },
    byLabel: { ...readMeter.byLabel },
    sessionDuration: Math.round((Date.now() - readMeter.sessionStart) / 1000)
  };
}

/**
 * Reset read meter
 */
export function resetReadMeter() {
  readMeter.total = 0;
  readMeter.byRoute = {};
  readMeter.byLabel = {};
  readMeter.sessionStart = Date.now();
  
  if (import.meta.env.DEV) {
    console.log('📊 READ METER: Reset');
  }
}

/**
 * Log summary of read statistics
 */
export function logReadSummary() {
  try {
    // Check if read meter is enabled (default to true in development)
    const isEnabled = import.meta.env?.VITE_READ_METER_ENABLED === 'true' || 
                     (import.meta.env?.DEV && import.meta.env?.VITE_READ_METER_ENABLED !== 'false');
    
    if (isEnabled) {
      console.group('📊 READ METER SUMMARY');
      console.log('Total reads:', readMeter.total);
      console.log('Session duration:', Math.round((Date.now() - readMeter.sessionStart) / 1000) + 's');
      console.table(readMeter.byRoute);
      console.table(readMeter.byLabel);
      console.groupEnd();
    }
  } catch (error) {
    console.warn('📊 READ METER: Error in logReadSummary:', error);
  }
}
